import type { User } from '@shady-pines/db';
import { isNonNil, type Uuid } from '@shady-pines/types';
import type { Session } from '@supabase/supabase-js';
import { isNil, omit } from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useApiStore } from '../api/api-store';
import { useEventStore } from '../event/event-store';
import { useGlobalContextStore } from '../global-context/global-context-store';
import { useShowStore } from '../show/show-store';
import { useTrackStore } from '../track/track-store';
import type { UserState } from './types';
import { UserApi } from './user-api';

const initialState = {
  loading: {
    myProfile: false,
    myMetadata: false,
    updateUserName: false,
    registerForPushNotifications: false,
    fetchingProfilesById: {},
    initial: true,
    searchingUsers: false,
  },

  completed: {
    session: false,
    myProfile: false,
  },

  me: null,
  oldMe: null,
  session: null,
  myProfile: null,
  isAdmin: false,
  profilesById: {},
  lastDataFetchedFromApiAt: null,
  userSearchCache: {},
} as const satisfies UserState;

type UtilityFunctions = {
  updateSession: (session: Session | null) => Promise<void>;
};

export const useUserStore = create<UserState & UserApi & UtilityFunctions>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...UserApi(get, set),

      updateSession: async (session) => {
        const sessionUser = session?.user;

        set(
          (s) =>
            ({
              session,
              loading: {
                ...s.loading,
                initial: false,
              },
              ...(isNil(sessionUser) || sessionUser.is_anonymous
                ? {
                    me: null,
                    myProfile: null,
                    isAdmin: false,
                  }
                : {
                    me: session!.user.id as Uuid<User>,
                  }),
            }) as const satisfies Partial<UserState>,
        );
        const { fetchMyProfile } = get();

        if (isNonNil(get().me) && isNil(get().myProfile)) {
          await fetchMyProfile();
        }
        // no need to trigger a re-fetch if the user did not actually change
        if (get().me !== get().oldMe) {
          useShowStore.setState({
            showSubscriptionsById: null,
            myShowSubscriptions: {},
          });
          useEventStore.setState({
            eventSubscriptionsById: null,
          });
          useTrackStore.setState({
            trackSubscriptionsById: null,
          });
          useGlobalContextStore.setState({
            userFeatures: null,
          });
        }

        set((s) => ({
          loading: {
            ...s.loading,
            initial: false,
          },
          oldMe: s.me,
        }));
      },
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const store = useApiStore.getState().localStores.user;
          return store?.getItem(name) ?? null;
        },
        setItem: (name: string, value: string) => {
          const store = useApiStore.getState().localStores.user;
          store?.setItem(name, value);
        },
        removeItem: (name: string) => {
          const store = useApiStore.getState().localStores.user;
          store?.removeItem(name);
        },
      })),
      skipHydration: true,
      partialize: (state) => omit(state, 'loading', 'session', 'completed', 'userSearchCache'),
    },
  ),
);
